import { parseVisitDate } from "./detailed-progress-utils";

export const MS_DAY = 24 * 60 * 60 * 1000;

export const COMPARISON_SERIES_COLORS = [
  "#46DC94",
  "#6366F1",
  "#F472B6",
  "#F59E0B",
  "#06B6D4",
  "#8B5CF6",
  "#EF4444",
  "#14B8A6",
];

const shortMonthLabel = (date) =>
  date
    .toLocaleDateString("pt-BR", { month: "short" })
    .replace(".", "")
    .replace(/^\w/, (char) => char.toUpperCase());

export const formatDayMonthLabel = (timestamp) => {
  const date = new Date(timestamp);
  return `${date.getDate()} ${shortMonthLabel(date)}`;
};

export const formatMonthLabel = (timestamp) => shortMonthLabel(new Date(timestamp));

export const timelineToPoints = (timeline = []) =>
  timeline
    .map((point) => {
      const date = parseVisitDate(point.visitDate);
      if (!date || point.value == null || !Number.isFinite(point.value)) {
        return null;
      }
      return {
        x: date.getTime(),
        y: point.value,
        visitDate: point.visitDate,
      };
    })
    .filter(Boolean)
    .sort((first, second) => first.x - second.x);

export const buildXTicks = (points, xMin, xMax) => {
  const spanDays = Math.max((xMax - xMin) / MS_DAY, 1);
  const ticks = new Set(points.map((point) => point.x));

  if (spanDays > 45) {
    const monthCursor = new Date(xMin);
    monthCursor.setDate(1);
    while (monthCursor.getTime() <= xMax) {
      if (monthCursor.getTime() >= xMin) {
        ticks.add(monthCursor.getTime());
      }
      monthCursor.setMonth(monthCursor.getMonth() + 1);
    }
  }

  if (ticks.size === 0) {
    ticks.add(xMin);
    if (xMax !== xMin) {
      ticks.add(xMax);
    }
  }

  return [...ticks].sort((a, b) => a - b);
};

export const formatXTick = (timestamp, spanDays, monthStarts) => {
  if (spanDays > 120) {
    return formatMonthLabel(timestamp);
  }

  if (monthStarts.has(timestamp)) {
    return formatMonthLabel(timestamp);
  }

  return formatDayMonthLabel(timestamp);
};

export const buildLinearXAxis = ({ points, xMin, xMax, spanDays, xTicks, monthStarts }) => ({
  type: "linear",
  min: xMin,
  max: xMax,
  offset: false,
  border: { display: false },
  grid: { display: false },
  afterBuildTicks: (scale) => {
    scale.ticks = xTicks.map((value) => ({ value }));
  },
  ticks: {
    color: "#9CA3AF",
    font: { size: 12, weight: "500" },
    padding: 8,
    maxRotation: 0,
    autoSkip: false,
    callback: (value) => formatXTick(Number(value), spanDays, monthStarts),
  },
});

export const resolveXAxisBounds = (allPoints) => {
  if (allPoints.length === 0) {
    return { xMin: 0, xMax: MS_DAY, spanDays: 1, xTicks: [0], monthStarts: new Set() };
  }

  const sortedX = [...new Set(allPoints.map((point) => point.x))].sort((a, b) => a - b);
  const xMin = sortedX[0];
  const xMax = sortedX.length === 1 ? sortedX[0] + MS_DAY : sortedX[sortedX.length - 1];
  const spanDays = Math.max((xMax - xMin) / MS_DAY, 1);
  const xTicks = buildXTicks(allPoints, xMin, xMax);
  const monthStarts = new Set(
    xTicks.filter((timestamp) => new Date(timestamp).getDate() === 1)
  );

  return { xMin, xMax, spanDays, xTicks, monthStarts };
};

export const resolveYAxisBounds = (yValues) => {
  if (yValues.length === 0) {
    return { suggestedMin: 0, suggestedMax: 1 };
  }

  const minValue = Math.min(...yValues);
  const maxValue = Math.max(...yValues);
  const padding = Math.max((maxValue - minValue) * 0.12, 0.5);

  return {
    suggestedMin: Math.max(0, minValue - padding),
    suggestedMax: maxValue + padding,
  };
};

export const createAreaGradient = (chart, borderColor) => {
  const { ctx, chartArea } = chart;
  if (!chartArea) {
    return borderColor;
  }
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, `${borderColor}47`);
  gradient.addColorStop(1, `${borderColor}00`);
  return gradient;
};
